import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, ShieldCheck, CheckCircle2, Zap } from 'lucide-react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useCmsImages, useCmsContent } from '../context/CmsImagesContext';

export default function About() {
  const { t } = useLanguage();
  const { getImage, getText, getHtml, content, companyInfo } = useCmsContent();

  useEffect(() => {
    document.title = t({ da: "FTR Service ApS - Om os", en: "FTR Service ApS - About Us" });
  }, [t]);

  return (
    <Layout>
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="w-full lg:w-1/2 relative">
              <div className="absolute -bottom-6 -left-6 w-64 h-64 bg-[#85d7f9]/20 rounded-3xl -z-0"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#85d7f9]/10 rounded-full blur-2xl -z-0"></div>
              
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl bg-white z-10">
                <img 
                  src={getImage('om_os_historie_billede', getImage('about_van', '/bilreklamer.jpeg', { width: 1000 }))} 
                  className="w-full h-auto block" 
                  alt={t({ da: "FTR Service professionel bilreklame", en: "FTR Service professional car advertising" })} 
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {getText('om_os', 'historie_overskrift', getText('om_os_hovedtekst', 'overskrift', t({ da: "Lær mig at kende", en: "Get to know me" })))}
              </h2>
              {getText('om_os', 'historie_tekst', getText('om_os_hovedtekst', 'tekst', null)) ? (
                <div 
                  className="space-y-6 text-lg text-gray-600 leading-relaxed [&>p]:mb-6"
                  dangerouslySetInnerHTML={{ __html: getHtml('om_os', 'historie_tekst', getHtml('om_os_hovedtekst', 'tekst', t({
                    da: '<p>FTR Service ApS er stiftet af Tamar Fechete, der kom til Danmark fra Rumænien for snart 12 år siden. Siden første dag i landet har arbejdet med rengøring været omdrejningspunktet, og mere end 10 års praktisk erfaring sikrer høj kvalitet, stabilitet og grundighed i alle opgaver.</p><p>For 3 år siden blev drømmen om at blive selvstændig til virkelighed med eget rengøringsfirma, som i år er vokset og blevet til FTR Service ApS.</p><p>Virksomheden tilbyder professionel rengøring til både private og erhverv, med fokus på tillid, fleksibilitet og langvarige samarbejder.</p><p>Med en stærk arbejdsmoral, mange års erfaring og en stor faglig stolthed leverer FTR Service ApS rengøringsløsninger, der skaber rene, trygge og indbydende omgivelser.</p>',
                    en: '<p>FTR Service ApS was founded by Tamar Fechete, who came to Denmark from Romania almost 12 years ago. Since the first day in the country, cleaning work has been the focus, and more than 10 years of practical experience ensures high quality, stability, and thoroughness in all tasks.</p><p>3 years ago, the dream of becoming self-employed became a reality with my own cleaning company, which this year has grown and become FTR Service ApS.</p><p>The company offers professional cleaning for both private and business, with a focus on trust, flexibility, and long-term collaborations.</p><p>With a strong work ethic, many years of experience, and great professional pride, FTR Service ApS delivers cleaning solutions that create clean, safe, and inviting surroundings.</p>'
                  }))) }}
                />
              ) : (
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                  <p>
                    {t({ 
                      da: "FTR Service ApS er stiftet af Tamar Fechete, der kom til Danmark fra Rumænien for snart 12 år siden. Siden første dag i landet har arbejdet med rengøring været omdrejningspunktet, og mere end 10 års praktisk erfaring sikrer høj kvalitet, stabilitet og grundighed i alle opgaver.",
                      en: "FTR Service ApS was founded by Tamar Fechete, who came to Denmark from Romania almost 12 years ago. Since the first day in the country, cleaning work has been the focus, and more than 10 years of practical experience ensures high quality, stability, and thoroughness in all tasks."
                    })}
                  </p>
                  <p>
                    {t({ 
                      da: "For 3 år siden blev drømmen om at blive selvstændig til virkelighed med eget rengøringsfirma, som i år er vokset og blevet til FTR Service ApS.",
                      en: "3 years ago, the dream of becoming self-employed became a reality with my own cleaning company, which this year has grown and become FTR Service ApS."
                    })}
                  </p>
                  <p>
                    {t({ 
                      da: "Virksomheden tilbyder professionel rengøring til både private og erhverv, med fokus på tillid, fleksibilitet og langvarige samarbejder.",
                      en: "The company offers professional cleaning for both private and business, with a focus on trust, flexibility, and long-term collaborations."
                    })}
                  </p>
                  <p>
                    {t({ 
                      da: "Med en stærk arbejdsmoral, mange års erfaring og en stor faglig stolthed leverer FTR Service ApS rengøringsløsninger, der skaber rene, trygge og indbydende omgivelser.",
                      en: "With a strong work ethic, many years of experience, and great professional pride, FTR Service ApS delivers cleaning solutions that create clean, safe, and inviting surroundings."
                    })}
                  </p>
                </div>
              )}
              <div className="mt-10">
                <Link to={getText('om_os', 'historie_knap_link', getText('om_os_hovedtekst', 'knap_link', '/tjenester'))} className="inline-flex items-center gap-2 text-sky-600 font-bold hover:gap-3 transition-all">
                  {getText('om_os', 'historie_knap_tekst', getText('om_os_hovedtekst', 'knap_tekst', t({ da: "Læs mere om ydelserne på servicesiden", en: "Read more about the services on the services page" })))} <Zap className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Projects and Safety Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 rounded-[2.5rem] overflow-hidden shadow-2xl border border-gray-100">
            <img 
              src={getImage('om_os_banner_billede', getImage('about_banner', '/lols.jpeg', { width: 1400 }))} 
              alt={t({ da: "FTR Service Professionel Rengøring", en: "FTR Service Professional Cleaning" })} 
              className="w-full h-[400px] object-cover"
            />
          </div>
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {getText('om_os', 'vaerdier_overskrift', getText('om_os_vaerdier_intro', 'overskrift', t({ da: "Projekter, erfaringer og værdier", en: "Projects, experiences and values" })))}
            </h2>
            <p className="text-lg text-gray-600">
              {getText('om_os', 'vaerdier_tekst', getText('om_os_vaerdier_intro', 'tekst', t({ da: "Tryg og fleksibel service til både private og erhverv", en: "Safe and flexible service for both private and business" })))}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 text-brand-primary rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {getText('om_os', 'privat_erhverv_overskrift', getText('om_os_privat_erhverv', 'overskrift', t({ da: "Privat & Erhverv", en: "Private & Business" })))}
                </h3>
              </div>
              {(content?.['om_os']?.privat_erhverv_tekst || content?.['om_os_privat_erhverv']?.tekst) ? (
                <div 
                  className="text-gray-600 text-lg leading-relaxed space-y-6 [&>p]:mb-6"
                  dangerouslySetInnerHTML={{ __html: getHtml('om_os', 'privat_erhverv_tekst', getHtml('om_os_privat_erhverv', 'tekst', t({
                    da: '<p>Virksomheden servicerer både private og erhvervskunder, fordelt nogenlunde ligeligt med cirka 50 % i hver gruppe. Alle aftaler tilpasses individuelt, så løsningen matcher den enkelte kundes behov, ønsker og hverdag.</p><p>Der udarbejdes faste aftaler, hvor omfang, frekvens og opgaver er tydeligt beskrevet, så der skabes gennemsigtighed og langvarige samarbejder. Fokus er altid på kvalitet, stabilitet og høj service.</p>',
                    en: '<p>The company serves both private and business customers, distributed roughly equally with approximately 50% in each group. All agreements are individually adapted so the solution matches the individual customer\'s needs, wishes, and everyday life.</p><p>Fixed agreements are drawn up where the scope, frequency, and tasks are clearly described, creating transparency and long-term collaborations. The focus is always on quality, stability, and high service.</p>'
                  }))) }}
                />
              ) : (
                <>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    {t({ 
                      da: "Virksomheden servicerer både private og erhvervskunder, fordelt nogenlunde ligeligt med cirka 50 % i hver gruppe. Alle aftaler tilpasses individuelt, så løsningen matcher den enkelte kundes behov, ønsker og hverdag.",
                      en: "The company serves both private and business customers, distributed roughly equally with approximately 50% in each group. All agreements are individually adapted so the solution matches the individual customer's needs, wishes, and everyday life."
                    })}
                  </p>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {t({ 
                      da: "Der udarbejdes faste aftaler, hvor omfang, frekvens og opgaver er tydeligt beskrevet, så der skabes gennemsigtighed og langvarige samarbejder. Fokus er altid på kvalitet, stabilitet og høj service.",
                      en: "Fixed agreements are drawn up where the scope, frequency, and tasks are clearly described, creating transparency and long-term collaborations. The focus is always on quality, stability, and high service."
                    })}
                  </p>
                </>
              )}
            </div>

            <div className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 text-brand-primary rounded-xl flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {getText('om_os', 'tryghed_overskrift', getText('om_os_tryghed', 'overskrift', t({ da: "Din tryghed er sikret", en: "Your safety is secured" })))}
                </h3>
              </div>
              {(content?.['om_os']?.tryghed_tekst || content?.['om_os_tryghed']?.tekst) ? (
                <div 
                  className="text-gray-600 text-lg leading-relaxed space-y-6 [&>p]:mb-6"
                  dangerouslySetInnerHTML={{ __html: getHtml('om_os', 'tryghed_tekst', getHtml('om_os_tryghed', 'tekst', t({
                    da: '<p>Tryghed vægtes meget højt, og arbejdet udføres altid med stor omhu og respekt for kundens ejendom.</p><p>Virksomheden er fuldt forsikret, så kunden er dækket, hvis uheld mod forventning skulle opstå. Det skaber ekstra sikkerhed og ro i maven for både private og erhvervskunder.</p>',
                    en: '<p>Safety is highly valued, and the work is always carried out with great care and respect for the customer\'s property.</p><p>The company is fully insured, so the customer is covered if an accident against expectations should occur. This creates extra security and peace of mind for both private and business customers.</p>'
                  }))) }}
                />
              ) : (
                <>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    {t({ 
                      da: "Tryghed vægtes meget højt, og arbejdet udføres altid med stor omhu og respekt for kundens ejendom.",
                      en: "Safety is highly valued, and the work is always carried out with great care and respect for the customer's property."
                    })}
                  </p>
                  <div className="bg-brand-primary/5 border border-brand-primary/10 p-6 rounded-2xl mb-6">
                    <p className="text-brand-primary font-medium leading-relaxed">
                      {t({ 
                        da: "Virksomheden er fuldt forsikret, så kunden er dækket, hvis uheld mod forventning skulle opstå. Det skaber ekstra sikkerhed og ro i maven for både private og erhvervskunder.",
                        en: "The company is fully insured, so the customer is covered if an accident against expectations should occur. This creates extra security and peace of mind for both private and business customers."
                      })}
                    </p>
                  </div>
                </>
              )}
              <div className="flex items-center gap-2 text-sky-600 font-bold mt-6">
                <CheckCircle2 className="w-5 h-5" />
                <span>{getText('om_os', 'tryghed_badge', getText('om_os_tryghed', 'underoverskrift', t({ da: "Professionel ansvarsforsikring", en: "Professional liability insurance" })))}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            {getText('om_os', 'cta_overskrift', getText('om_os_cta', 'overskrift', t({ da: "Vi glæder os til at høre fra dig", en: "We look forward to hearing from you" })))}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-12">
            {getText('om_os', 'cta_tekst', getText('om_os_cta', 'tekst', t({ 
              da: "Uanset om du har brug for en fast aftale til din virksomhed eller hjælp i det private hjem, står vi klar med en løsning der passer dig.",
              en: "Whether you need a fixed agreement for your business or help in the private home, we are ready with a solution that suits you."
            })))}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to={getText('om_os', 'cta_knap_link', getText('om_os_cta', 'knap_link', '/kontakt'))} className="px-10 py-4 bg-[#85d7f9] text-white rounded-full font-bold hover:opacity-90 transition-all shadow-lg shadow-[#85d7f9]/20">
              {getText('om_os', 'cta_knap_tekst', getText('om_os_cta', 'knap_tekst', t({ da: "Kontakt os i dag", en: "Contact us today" })))}
            </Link>
            <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="px-10 py-4 bg-gray-100 text-gray-900 rounded-full font-bold hover:bg-gray-200 transition-all">
              {t({ da: "Ring til os", en: "Call us" })}: {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
